import { gql, useQuery } from '@apollo/client'
import * as d3 from 'd3-scale'
import * as _ from 'lodash'
import { useMemo } from 'react'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { IApiDataType } from '../../types'

interface IDataType {
  trendLocationPurchase: {
    nodes: {
      startDate: string
      endDate: string
      businessQuarter: number
      businessMonth: number
      businessWeek: number
      netSales: number
      itemDetails: Record<
        string,
        {
          type: 'all' | 'item' | 'category'
          item: string
          actualUsage: number
          actualUsagePercent: number
          end: number
          percent: number
        }
      >
    }[]
  }
}

const query = gql`
  query trendLocationPurchase($iLocationId: Int!, $iEndDate: Date!) {
    trendLocationPurchase(
      iLocationId: $iLocationId
      iEndDate: $iEndDate
      iGroupBy: "last_x_weeks"
      iDateRangeNumber: 4
    ) {
      nodes {
        startDate
        endDate
        businessQuarter
        businessMonth
        businessWeek
        netSales
        itemDetails
      }
    }
  }
`

export const bbbLocationPurchaseByCategoryConfigs = {
  itemName: 'string',
  last4Week: 'percent',
  last3Week: 'percent',
  last2Week: 'percent',
  last1Week: 'percent',
  last0Week: 'percent',
} as const

const useBbbLocationPurchaseByCategory = () => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()
  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iLocationId: groupFilter?.ids[0],
      iEndDate: endDate,
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo((): IApiDataType => {
      const trendData = data?.trendLocationPurchase.nodes

      if (!data || !trendData || trendData?.length === 0) return null

      const getHeatMap = (item: string, value: number) => {
        if (_.isNil(value)) return null

        const values = trendData.reduce((res, td) => {
          if (td.itemDetails) res.push(td.itemDetails[item]?.actualUsagePercent)

          return res
        }, [] as number[])

        if (Math.min(...values) === Math.max(...values)) return 0

        const x = d3.scaleLinear(
          [0, 100],
          [Math.min(...values), Math.max(...values)],
        )

        return x.invert(value)
      }

      const allData = Object.values(trendData[0].itemDetails)
        .filter((i) => i.type === 'category')
        .map((d) =>
          trendData.reduce(
            (result, { itemDetails }, index) => {
              return {
                ...result,
                [`last${4 - index}Week`]:
                  itemDetails[d.item]?.actualUsagePercent,
                [`last${4 - index}WeekHeatMap`]: getHeatMap(
                  d.item,
                  itemDetails[d.item]?.actualUsagePercent,
                ),
              }
            },
            {
              id: d.item,
              parentId: 'summary',
              itemName: d.item,
            } as {
              id: string
              parentId: string
              itemName: string
              last0Week: number
              last1Week: number
              last2Week: number
              last3Week: number
              last4Week: number
            },
          ),
        )
      const summary = allData.reduce(
        (result, d) => ({
          ...result,
          last0Week: result.last0Week + d.last0Week,
          last1Week: result.last1Week + d.last1Week,
          last2Week: result.last2Week + d.last2Week,
          last3Week: result.last3Week + d.last3Week,
          last4Week: result.last4Week + d.last4Week,
        }),
        {
          id: 'summary',
          parentId: 'root',
          itemName: 'Total',
          last0Week: 0,
          last1Week: 0,
          last2Week: 0,
          last3Week: 0,
          last4Week: 0,
        },
      )

      return [...allData, summary]
    }, [data]),
    loading,
  }
}

export default useBbbLocationPurchaseByCategory
